import {
  BulbOutlined,
  CodeOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { WarningTwoTone } from "@ant-design/icons";
import { RoutingRule } from "@p0security/shared/types/workflow/types";
import { Alert, Button, Segmented, Spin } from "antd";
import { ErrorDisplay } from "components/Error";
import RecommendedPolicies from "components/RecommendedPolicies";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { useFlags } from "launchdarkly-react-client-sdk";
import { noop } from "lodash";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useRoutingRulesStore } from "../../../store/routingRulesStore";
import ContactSupport from "../../Integrations/components/ContactSupport";
import { useHasRole } from "../../Login/hook";
import { InstalledIntegrationsProvider } from "./providers/InstalledIntegrationsProvider";
import { useLoadRoutingRules } from "./useLoadRoutingRules";
import { useSubmitWorkflow } from "./useSubmitWorkflow";
import { RulesView } from "./views/RulesView";
import TableView from "./views/TableView";

const StyledRoutingRulesContainer = styled.div`
  .capitalize {
    text-transform: capitalize;
  }
`;

type ActiveView = "recommended" | "table" | "yaml";

export const RoutingRules: React.FC = () => {
  const flags = useFlags();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ActiveView>("table");
  const { currentVersion, errors, isLoading, jsonRules, setErrors } =
    useRoutingRulesStore();

  const submitWorkflow = useSubmitWorkflow();

  useLoadRoutingRules();

  const canSubmit = useHasRole("owner");

  const handleAddNewPolicy = useCallback(() => {
    navigate("new");
  }, [navigate]);

  const handleSubmitWorkflow = useCallback(
    async (value: string) => {
      if (!flags.requestRouting) {
        return;
      }
      try {
        await submitWorkflow({
          jsonData: {
            currentVersion,
            workflow: { rules: yaml.load(value) as RoutingRule[] },
          },
          shouldNavigate: true,
        });
      } catch (err) {
        setErrors([String(err)]);
      }
    },
    [currentVersion, flags.requestRouting, setErrors, submitWorkflow]
  );

  const handleDelete = useCallback(
    async (record: RoutingRule) => {
      const updatedRules = jsonRules.filter(
        (rule) => stringify(rule) !== stringify(record)
      );
      const policyLabel = record.name ? `Policy "${record.name}"` : "Policy";
      await submitWorkflow({
        jsonData: { currentVersion, workflow: { rules: updatedRules } },
        shouldNavigate: false,
        successMessage: `${policyLabel} was successfully deleted.`,
      });
    },
    [currentVersion, jsonRules, submitWorkflow]
  );

  return (
    <InstalledIntegrationsProvider>
      <StyledRoutingRulesContainer>
        {errors.map((e, ix) => (
          <ErrorDisplay error={e} key={ix} onClose={noop} />
        ))}

        {isLoading ? (
          <Spin />
        ) : (
          <>
            {!flags.requestRouting && (
              <Alert
                message={
                  <>
                    Custom routing is not available in your current plan. Please
                    <ContactSupport /> to enable. You can still configure basic
                    routing using the settings page.
                  </>
                }
                type="warning"
                icon={<WarningTwoTone twoToneColor="#faad14" />}
                showIcon
              />
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Segmented
                options={[
                  {
                    label: "Table View",
                    value: "table",
                    icon: <TableOutlined />,
                  },
                  { label: "YAML View", value: "yaml", icon: <CodeOutlined /> },
                  {
                    label: "Recommended",
                    value: "recommended",
                    icon: <BulbOutlined />,
                  },
                ]}
                value={activeView}
                onChange={setActiveView}
              />
              {canSubmit && activeView !== "recommended" && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddNewPolicy}
                >
                  Add New Policy
                </Button>
              )}
            </div>
            {activeView === "recommended" ? (
              <RecommendedPolicies />
            ) : activeView === "table" ? (
              <TableView canSubmit={canSubmit} handleDelete={handleDelete} />
            ) : (
              <RulesView
                canSubmit={canSubmit}
                handleSubmitWorkflow={handleSubmitWorkflow}
              />
            )}
          </>
        )}
      </StyledRoutingRulesContainer>
    </InstalledIntegrationsProvider>
  );
};
